import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import {
  AddSearchMessageToQueueRequest,
  GetNestedSearchesParams,
  GetNestedSearchesResponse,
  NestedChildSearchItem,
  PendingSearchResponse,
  SearchRequest,
} from '../../../api/types/sessionSearch.type';
import {NestedSearchGridRow} from '../../../api/types/nestedsearch.types';
import {t} from 'i18next';

dayjs.extend(utc);

export const NESTED_SEARCH_CONTEXT_TYPE = {
  SEARCH: 'SEARCH',
  SEARCHGROUP: 'SEARCHGROUP',
} as const;

export const NESTED_SEARCH_TYPE_CODE = {
  SAVED: 'SAVED',
  SESSION: 'SESSION',
} as const;

export type NestedSearchModalAction = 'small' | 'large';

export type NestedSearchFlowContext = 'session' | 'saved';

export interface NestedSearchExecutionContext {
  searchSetGUID: string;
}

export type SessionRunSearchFlowResult =
  | {outcome: 'error'; messages: string[]}
  | {
      outcome: 'proceed';
      queueRequest: AddSearchMessageToQueueRequest;
      messages: string[];
    }
  | {
      outcome: 'small';
      queueRequest: AddSearchMessageToQueueRequest;
      messages: string[];
    }
  | {
      outcome: 'large';
      queueRequest: AddSearchMessageToQueueRequest;
      rows: NestedChildSearchItem[];
      messages: string[];
    };

/** Saved search: /nested-searches response — ignores addSearchMessageToQueueRequest */
export type NestedSearchFlowResult =
  | {outcome: 'error'; messages: string[]}
  | {
      outcome: 'proceed';
      context: NestedSearchExecutionContext;
      messages: string[];
    }
  | {
      outcome: 'small';
      context: NestedSearchExecutionContext;
      pendingCount: number;
      messages: string[];
    }
  | {
      outcome: 'large';
      context: NestedSearchExecutionContext;
      rows: NestedChildSearchItem[];
      messages: string[];
    };

export const CIRCULAR_REFERENCE_ERROR_CODE = '42001000001';
export const PENDING_SEARCH_ERROR_CODE = '42001000002';
export const PENDING_SEARCH_IDS_VISIBLE_LIMIT = 10;

export function parsePendingSearchIds(
  pendingSearches?: string | null
): string[] {
  return String(pendingSearches ?? '')
    .split(',')
    .map((id) => id.trim())
    .filter((id) => id.length > 0);
}

export function formatPendingSearchIdsForErrorMessage(
  pendingSearches?: string | null,
  maxVisible = PENDING_SEARCH_IDS_VISIBLE_LIMIT
): string {
  const ids = parsePendingSearchIds(pendingSearches);
  if (ids.length === 0) {
    return '';
  }

  if (ids.length > maxVisible) {
    return `${ids.slice(0, maxVisible).join(', ')}, ...`;
  }

  return ids.join(', ');
}

export function normalizePendingSearchDetails(
  details: GetNestedSearchesResponse['pendingSearchDetails']
): PendingSearchResponse[] {
  if (details == null) {
    return [];
  }
  return Array.isArray(details) ? details : [details];
}

export function getShouldProceed(result: GetNestedSearchesResponse): boolean {
  return Boolean(result.shouldProceed ?? result.ShouldProceed);
}

export function getShouldShowPopup(result: GetNestedSearchesResponse): boolean {
  return Boolean(result.shouldShowPopup ?? result.ShouldShowPopup);
}

/**
 * Builds toast messages from pendingSearchDetails.
 * Only entries with non-empty pendingSearches (search IDs) produce a toast.
 * Callers should only surface these when ShouldProceed is false.
 */
export function getNestedSearchErrorMessages(
  pendingSearchDetails: GetNestedSearchesResponse['pendingSearchDetails'],
  context: NestedSearchFlowContext
): string[] {
  const messages: string[] = [];

  for (const detail of normalizePendingSearchDetails(pendingSearchDetails)) {
    const searchIds = formatPendingSearchIdsForErrorMessage(
      detail.pendingSearches
    );
    // Note 3: only display toast when search IDs are present
    if (!searchIds) {
      continue;
    }

    const code = String(detail.error_Code ?? '').trim();

    if (code === CIRCULAR_REFERENCE_ERROR_CODE) {
      if (context === 'session') {
        messages.push(t('sessionSearch.errors.circularReference', {searchIds}));
      } else {
        messages.push(
          t('savedSearch.executeNestedSearch.errors.circularReference', {
            searchIds,
          })
        );
      }
      continue;
    }

    if (code === PENDING_SEARCH_ERROR_CODE || code.length > 0) {
      if (context === 'session') {
        messages.push(
          t('sessionSearch.errors.pendingSearchWithIds', {searchIds})
        );
      } else {
        messages.push(
          t('savedSearch.executeNestedSearch.errors.pendingSearchWithIds', {
            searchIds,
          })
        );
      }
    }
  }

  return messages;
}

export function getPendingSearchCount(pendingSearches?: string | null): number {
  return parsePendingSearchIds(pendingSearches).length;
}

export function getTotalPendingSearchCount(
  pendingSearchDetails: GetNestedSearchesResponse['pendingSearchDetails']
): number {
  return normalizePendingSearchDetails(pendingSearchDetails).reduce(
    (sum, detail) => sum + getPendingSearchCount(detail.pendingSearches),
    0
  );
}

/** @deprecated Prefer getNestedSearchErrorMessages + ShouldProceed flow */
export function getNestedSearchPendingErrorMessage(
  errorCode: string | number,
  pendingSearches?: string,
  context: NestedSearchFlowContext = 'saved'
): string {
  const messages = getNestedSearchErrorMessages(
    [{pendingSearches: pendingSearches ?? '', error_Code: String(errorCode)}],
    context
  );
  return messages[0] ?? t('savedSearch.executeNestedSearch.executeError');
}

/** @deprecated Prefer ShouldProceed-based flow */
export function hasPendingSearchError(
  input:
    | string
    | number
    | null
    | undefined
    | PendingSearchResponse
    | PendingSearchResponse[]
): boolean {
  if (input === null || input === undefined) {
    return false;
  }

  if (Array.isArray(input)) {
    return input.some((item) => hasPendingSearchError(item));
  }

  if (typeof input === 'object') {
    const pendingSearches = String(input.pendingSearches ?? '').trim();
    // Only treat as toastable/blocking error when search IDs are present
    return pendingSearches.length > 0;
  }

  const code = String(input).trim();
  return code.length > 0 && code !== '0';
}

export function buildAddSearchToQueueRequest(
  result: GetNestedSearchesResponse,
  searchRequest?: SearchRequest
): AddSearchMessageToQueueRequest | null {
  const queueRequest = result.addSearchMessageToQueueRequest;
  if (!queueRequest) {
    return null;
  }

  const searchSetGUID = result.searchSetGUID || queueRequest.setId || '';
  if (!searchSetGUID) {
    return null;
  }

  const searchDetails: SearchRequest = searchRequest
    ? {
        ...searchRequest,
        searchSetGUID,
        advSearchID:
          queueRequest.searchDetails?.advSearchID ?? searchRequest.advSearchID,
      }
    : {
        ...queueRequest.searchDetails,
        searchSetGUID,
      };

  return {
    ...queueRequest,
    setId: searchSetGUID,
    searchDetails,
  };
}

/**
 * Session search: /runsearch response → error | proceed | small | large */
export function resolveSessionRunSearchFlow(
  result: GetNestedSearchesResponse,
  searchRequest?: SearchRequest
): SessionRunSearchFlowResult {
  const shouldProceed = getShouldProceed(result);
  const shouldShowPopup = getShouldShowPopup(result);
  // Circular/pending error toasts only when the API blocks proceed
  const messages = shouldProceed
    ? []
    : getNestedSearchErrorMessages(result.pendingSearchDetails, 'session');

  if (!shouldProceed) {
    return {
      outcome: 'error',
      messages:
        messages.length > 0
          ? messages
          : [t('savedSearch.executeNestedSearch.executeError')],
    };
  }

  const queueRequest = buildAddSearchToQueueRequest(result, searchRequest);
  if (!queueRequest) {
    return {
      outcome: 'error',
      messages: [t('savedSearch.executeNestedSearch.executeError')],
    };
  }

  if (shouldShowPopup) {
    const notExecuted = normalizeNestedChildSearchItems(
      result.searchesNotExecuted
    );
    if (notExecuted.length > 0) {
      return {
        outcome: 'large',
        queueRequest,
        rows: notExecuted,
        messages,
      };
    }
    return {outcome: 'small', queueRequest, messages};
  }

  return {outcome: 'proceed', queueRequest, messages};
}

/**
 * Saved search: /nested-searches → background/queue (ignore addSearchMessageToQueueRequest) */
export function resolveNestedSearchFlow(
  result: GetNestedSearchesResponse
): NestedSearchFlowResult {
  const shouldProceed = getShouldProceed(result);
  const shouldShowPopup = getShouldShowPopup(result);
  // Circular/pending error toasts only when the API blocks proceed
  const messages = shouldProceed
    ? []
    : getNestedSearchErrorMessages(result.pendingSearchDetails, 'saved');

  if (!shouldProceed) {
    return {
      outcome: 'error',
      messages:
        messages.length > 0
          ? messages
          : [t('savedSearch.executeNestedSearch.executeError')],
    };
  }

  const context = getNestedSearchExecutionContext(result);
  if (!context?.searchSetGUID) {
    return {
      outcome: 'error',
      messages: [t('savedSearch.executeNestedSearch.executeError')],
    };
  }

  if (shouldShowPopup) {
    const notExecuted = normalizeNestedChildSearchItems(
      result.searchesNotExecuted
    );
    if (notExecuted.length > 0) {
      return {
        outcome: 'large',
        context,
        rows: notExecuted,
        messages,
      };
    }
    return {
      outcome: 'small',
      context,
      pendingCount: getTotalPendingSearchCount(result.pendingSearchDetails),
      messages,
    };
  }

  return {outcome: 'proceed', context, messages};
}

export function getNestedSearchExecutionContext(
  result: GetNestedSearchesResponse
): NestedSearchExecutionContext | null {
  if (!result.searchSetGUID) {
    return null;
  }

  return {
    searchSetGUID: result.searchSetGUID,
  };
}

export function formatNestedSearchContextId(
  ids: Array<string | number> | string | number
): string {
  const normalizedIds = Array.isArray(ids)
    ? ids
    : String(ids)
        .split(',')
        .map((id) => id.trim());

  return normalizedIds
    .map((id) => String(id).trim())
    .filter((id) => id.length > 0 && Number(id) > 0)
    .join(',');
}

/** Active/primary group id for background/queue; count uses formatSavedSearchGroupContextId */
export function getSavedSearchGroupId(
  selectedItem: string | null | undefined
): number {
  return Number(selectedItem);
}

/** Comma-separated SEARCHGROUP contextId; preserves ids like -1 (My Searches). */
export function formatSavedSearchGroupContextId(
  groupIds: Array<string | number | null | undefined>
): string {
  return groupIds
    .map((id) => String(id ?? '').trim())
    .filter((id) => id.length > 0 && Number.isFinite(Number(id)))
    .join(',');
}

export function resolveSavedSearchNestedParams(options: {
  searchId?: number | null;
  searchGroupId?: number | null;
  searchGroupIds?: Array<string | number | null | undefined>;
}): GetNestedSearchesParams | null {
  const searchId = options.searchId;
  if (searchId != null && Number.isFinite(searchId) && searchId > 0) {
    return {
      contextId: String(searchId),
      contextType: NESTED_SEARCH_CONTEXT_TYPE.SEARCH,
      searchTypeCode: NESTED_SEARCH_TYPE_CODE.SAVED,
    };
  }

  const groupContextId =
    options.searchGroupIds && options.searchGroupIds.length > 0
      ? formatSavedSearchGroupContextId(options.searchGroupIds)
      : options.searchGroupId != null && Number.isFinite(options.searchGroupId)
        ? String(options.searchGroupId)
        : '';

  if (!groupContextId) {
    return null;
  }

  return {
    contextId: groupContextId,
    contextType: NESTED_SEARCH_CONTEXT_TYPE.SEARCHGROUP,
    searchTypeCode: NESTED_SEARCH_TYPE_CODE.SAVED,
  };
}

export function applyNestedSearchExecutionContext<
  T extends {searchSetGUID?: string},
>(payload: T, context: NestedSearchExecutionContext | null): T {
  if (!context) {
    return payload;
  }

  return {
    ...payload,
    searchSetGUID: context.searchSetGUID,
  };
}

const formatNestedSearchLastCompleteTime = (dateString: string): string => {
  if (!dateString) return '';
  const date = dayjs(dateString);

  if (!date.isValid()) return '';

  return date.format('YYYY/MM/DD HH:mm:ss');
};

export const normalizeNestedChildSearchItems = (
  items: NestedChildSearchItem[] | null | undefined
): NestedChildSearchItem[] => items ?? [];

export const mapNestedSearchItemsToGridRows = (
  items: NestedChildSearchItem[]
): NestedSearchGridRow[] =>
  (items ?? []).map((item) => ({
    searchID: item.searchID,
    id: String(item.searchID).padStart(4, '0'),
    name: item.searchName,
    lastCompleteTime: formatNestedSearchLastCompleteTime(item.lastExecutedOn),
    searchGroup: item.searchGroupName ?? '',
  }));

export const NESTED_SEARCH_GRID_HEADER_HEIGHT = 48;
export const NESTED_SEARCH_GRID_MAX_VISIBLE_ROWS = 10;

export const getNestedSearchGridHeight = (
  _rowCount: number,
  rowHeight = 36
): number => {
  return (
    NESTED_SEARCH_GRID_HEADER_HEIGHT +
    NESTED_SEARCH_GRID_MAX_VISIBLE_ROWS * rowHeight
  );
};

export const getNestedSearchGridContainerMinHeight = (
  rowHeight = 36
): number => {
  return (
    NESTED_SEARCH_GRID_HEADER_HEIGHT +
    NESTED_SEARCH_GRID_MAX_VISIBLE_ROWS * rowHeight
  );
};

const escapeCsvField = (value: string): string => {
  if (/[",\n]/.test(value)) {
    return `"${value.replace(/"/g, '""')}"`;
  }
  return value;
};

export const exportNestedSearchesToCsv = (
  rows: NestedSearchGridRow[],
  fileName = 'nested-searches.csv'
): void => {
  const headers = ['ID', 'Search Name', 'Last Complete Time', 'Search Group'];
  const csvLines = [
    headers.join(','),
    ...rows.map((row) =>
      [
        escapeCsvField(row.id),
        escapeCsvField(row.name),
        escapeCsvField(row.lastCompleteTime),
        escapeCsvField(row.searchGroup),
      ].join(',')
    ),
  ];

  const blob = new Blob([csvLines.join('\n')], {
    type: 'text/csv;charset=utf-8;',
  });
  const url = window.URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = fileName;
  document.body.appendChild(anchor);
  anchor.click();
  window.URL.revokeObjectURL(url);
  document.body.removeChild(anchor);
};
